"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/cn";

export type ToastVariant = "default" | "success" | "danger" | "info";

export interface ToastOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  /** Auto-dismiss delay in ms. Defaults to 4000. */
  duration?: number;
  /** Optional inline action (e.g. undo). */
  action?: { label: React.ReactNode; onClick: () => void };
}

interface ToastItem extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

const accents: Record<ToastVariant, string> = {
  default: "before:bg-flamingo",
  success: "before:bg-success",
  danger: "before:bg-danger",
  info: "before:bg-info",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const tCommon = useTranslations("common");
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = ++idRef.current;
      setToasts((list) => [...list, { ...options, id }]);
      window.setTimeout(() => dismiss(id), options.duration ?? 4000);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className={cn(
          // z-[98]: above the lightbox stack (z-[95] lightbox / z-[96] sheet /
          // z-[97] dialogs) so engagement failure toasts stay visible over it.
          "pointer-events-none fixed inset-x-0 z-[98] flex flex-col items-center gap-2.5 px-4",
          // above the mobile tab bar; top-right on desktop
          "bottom-[calc(5.5rem+env(safe-area-inset-bottom))]",
          "sm:top-5 sm:right-5 sm:bottom-auto sm:left-auto sm:items-end sm:px-0",
        )}
        role="region"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "glass-panel pointer-events-auto flex w-full max-w-sm animate-rise items-start gap-3 overflow-hidden rounded-2xl bg-elevated/90 py-3.5 pr-3.5 pl-5 shadow-lift",
              "relative before:absolute before:inset-y-2.5 before:left-0 before:w-1 before:rounded-full",
              accents[t.variant ?? "default"],
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">{t.title}</p>
              {t.description && (
                <p className="mt-0.5 text-xs leading-snug text-white/60">
                  {t.description}
                </p>
              )}
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    t.action?.onClick();
                    dismiss(t.id);
                  }}
                  className="mt-2 text-xs font-semibold text-flamingo-light underline-offset-2 hover:underline"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label={tCommon("close")}
              className="relative -mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-white/45 transition-colors after:absolute after:-inset-2 hover:bg-white/8 hover:text-white"
            >
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
